/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DotsHorizontalIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import debounce from 'lodash.debounce';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/use-toast';

import { IWithPagination, UserData } from '@/commons/commons';
import Pagination from '@/components/list/Pagination';
import { useGet, useMutate, useUser } from '@/net/commons';
import { cn } from '@/utils';

type UserRowActionsProps = {
    user: UserData;
    toggleImpersonate: () => void;
};

const UserRowActions = ({ user, toggleImpersonate }: UserRowActionsProps) => {
    return (
        <AlertDialog>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-7 w-7 p-0 data-[state=open]:bg-muted">
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[160px]">
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem>Impersonate</DropdownMenuItem>
                    </AlertDialogTrigger>
                </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Impersonate User?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Are you sure you want to impersonate{' '}
                        <span className="font-semibold text-black">{user.email}</span>?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={toggleImpersonate}>
                        Yes, Impersonate
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default function UsersTable() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 20;
    const searchQuery = searchParams.get('search') || '';
    const [searchInputValue, setSearchInputValue] = useState(searchQuery);
    const { data: users } = useGet<IWithPagination<UserData>>(
        // eslint-disable-next-line max-len
        `/bff/users?page=${page}&pageSize=${pageSize}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`,
    );
    const { user } = useUser();
    const { toast } = useToast();
    const { trigger: impersonate } = useMutate<{ url: string }>('/bff/users/:id/impersonate', {
        method: 'POST',
    });
    const [impersonationUrl, setImpersonationUrl] = useState<string | null>(null);
    const [showUrlDialog, setShowUrlDialog] = useState(false);

    const shouldShowPagination = (users?.pagination?.total || 0) > pageSize;

    const debouncedSetSearchQuery = useCallback(
        debounce((value: string) => {
            const newSearchParams = new URLSearchParams(searchParams);
            if (value) {
                newSearchParams.set('search', value);
            } else {
                newSearchParams.delete('search');
            }
            setSearchParams(newSearchParams);
        }, 300),
        [searchParams, setSearchParams],
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInputValue(value);
        debouncedSetSearchQuery(value);
    };

    const copyTokenToClipboard = () => {
        if (impersonationUrl) {
            navigator.clipboard
                .writeText(impersonationUrl)
                .then(() => {
                    toast({
                        title: 'Url Copied',
                        description: 'Impersonation url copied to clipboard.',
                    });
                })
                .catch(() => {
                    toast({
                        title: 'Copy Failed',
                        description: 'Failed to copy url. Please select and copy manually.',
                        variant: 'destructive',
                    });
                });
        }
    };

    const onImpersonate = async (id: number) => {
        // Once staging env is available with the same clerk app
        // we'll remove this check
        if (window.location.hostname === 'app-staging.heal.dev') {
            toast({
                title: 'Impersonation Disabled',
                description: 'Impersonation is disabled in staging environment.',
                variant: 'destructive',
            });
            return;
        }

        const res = await impersonate({ idParam: id });
        setImpersonationUrl(res.url);
        setShowUrlDialog(true);
    };

    return (
        <>
            <div className="flex items-center">
                <div className="relative w-full max-w-sm">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Search by name or email..."
                        className="pl-8"
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[45%]">User</TableHead>
                        <TableHead className="w-[25%]">Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users?.data.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                                There are no items to display.
                            </TableCell>
                        </TableRow>
                    )}
                    {users?.data.map((row) => {
                        const fullName =
                            row.firstName || row.lastName ? `${row.firstName} ${row.lastName}`.trim() : null;
                        return (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            {fullName && (
                                                <span className="capitalize text-sm font-medium">{fullName}</span>
                                            )}
                                            {row.id === user?.id && (
                                                <Badge
                                                    variant="outline"
                                                    className="bg-accent text-accent-foreground px-1.5 py-[1px]"
                                                >
                                                    You
                                                </Badge>
                                            )}
                                        </div>
                                        <span className={cn(fullName ? 'text-xs' : 'text-sm font-medium')}>
                                            {row.email}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs">
                                    {new Date(row.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="flex justify-end">
                                    {row.id !== user?.id && (
                                        <UserRowActions user={row} toggleImpersonate={() => onImpersonate(row.id)} />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {users?.pagination && shouldShowPagination && <Pagination {...users.pagination} />}
            <Dialog open={showUrlDialog} onOpenChange={setShowUrlDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Impersonation Link Ready</DialogTitle>
                        <DialogDescription>Please follow these steps to impersonate the user:</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <ol className="list-decimal pl-5 space-y-2">
                            <li>Open a new private/incognito window in your browser</li>
                            <li>Copy the impersonation link below</li>
                            <li>Paste and navigate to the link in your private window</li>
                        </ol>

                        <div className="space-y-1">
                            <p className="text-sm font-medium">Impersonation Link:</p>
                            <div className="flex space-x-2">
                                <Input value={impersonationUrl as string} readOnly className="font-mono text-xs" />
                                <Button onClick={copyTokenToClipboard} type="button" variant="secondary">
                                    Copy
                                </Button>
                            </div>
                        </div>

                        <div className="bg-amber-50 p-3 rounded border border-amber-200">
                            <p className="text-sm text-amber-800">
                                <strong>Important:</strong> This link is valid for a limited time and should not be
                                shared. For security reasons, use a private/incognito window to avoid mixing with your
                                current session.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowUrlDialog(false)} type="button">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
